import { useAuth, useWS } from "@hooks";
import { useEffect, useEffectEvent } from "react";

interface WsMessage {
  event?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

interface UseWatchUserWsEventProps {
  onMessage: (message: WsMessage) => void;
  eventName?: string;
  onConnect?: () => void;
}

const WS_CONNECT_WAIT = 10000;
/**
 * Subscribes to WebSocket events for the current user.
 *
 * Uses `useEffectEvent` so callers can pass inline or unstable callbacks
 * without causing the WS effect to re-run. The effect only re-runs on
 * actual connect/reconnect (`userWsConnection` changes), not on callback
 * identity changes. This avoids infinite loops when callbacks capture
 * unstable values like Apollo refetch functions.
 *
 * For debounced callbacks (e.g. companion), use `useDebouncedCallback`
 * which handles stable identity separately — `useEffectEvent` can't be
 * called from event handlers (onClick, etc.).
 */
export function useWatchUserWsEvent({
  onMessage,
  eventName,
  onConnect = () => {},
}: UseWatchUserWsEventProps) {
  const { connectWS, webSockets } = useWS();
  const { userUuid } = useAuth();

  const onMessageEvent = useEffectEvent(onMessage);
  const onConnectEvent = useEffectEvent(onConnect);
  const connect = useEffectEvent(connectWS);

  const channel = userUuid ? `user/${userUuid}` : null;
  const userWsConnection = channel ? webSockets[channel] : null;

  useEffect(() => {
    if (!channel) return;

    if (!userWsConnection) {
      connect(channel);
      return;
    }

    setTimeout(onConnectEvent, WS_CONNECT_WAIT * Math.random());

    const handler = (e: MessageEvent) => {
      const message = JSON.parse(e.data);
      if (!eventName || message.event === eventName) {
        onMessageEvent(message);
      }
    };

    userWsConnection.addEventListener("message", handler);
    return () => {
      userWsConnection.removeEventListener("message", handler);
    };
  }, [channel, userWsConnection, eventName]);
}
